import { trackEvent } from "src/utils/api/tracker";
import { isRequestSuccess } from "src/utils/redux/requestState";
import getStore from "src/utils/redux/store";
import {
  BRANDED_ASYNC_PRODUCT_NAME,
  BRANDED_SYNC_PRODUCT_NAME,
} from "src/utils/medProductNames";
import {
  getProductEligibility,
  isClinicalVendorSegment,
  loadProductEligibility,
} from "src/utils/redux/slices/productEligibility";

const MED_PRODUCT_ELIGIBILITY_PRODUCT_NAMES = [
  BRANDED_SYNC_PRODUCT_NAME,
  BRANDED_ASYNC_PRODUCT_NAME,
];

function trackClinicalVendorEligibilityGate() {
  const state = getStore().getState();
  const products: Record<string, Record<string, unknown>> = {};

  for (const productName of MED_PRODUCT_ELIGIBILITY_PRODUCT_NAMES) {
    const entry = getProductEligibility(productName, state);
    if (!isRequestSuccess(entry)) {
      products[productName] = { eligibilityState: null };
    } else {
      const purchaseEligibility = entry.data?.purchaseEligibility;
      const ineligibleDetails = purchaseEligibility?.ineligibleDetails ?? [];
      const maybeEligibleDetails =
        purchaseEligibility?.maybeEligibleDetails ?? [];

      const vendorConflict = ineligibleDetails.find(isClinicalVendorSegment);

      products[productName] = {
        eligibilityState: purchaseEligibility?.eligibilityState ?? null,
        vendorConflictSegmentId: vendorConflict?.ectSegmentId ?? null,
        vendorSegmentErrored: maybeEligibleDetails.some(
          isClinicalVendorSegment
        ),
        ineligibleSegmentIds: ineligibleDetails
          .map((detail) => detail.ectSegmentId)
          .filter(Boolean),
      };
    }
  }

  trackEvent("ClinicalVendorEligibilityGate", { products });
}

export function dispatchMedProductEligibilityLoad(
  dispatch: ReturnType<typeof getStore>["dispatch"]
) {
  // The vendor verdict derives from the user's existing treatment and cannot
  // change mid-flow, so a resolved verdict never needs a refetch. Fetch only
  // the unresolved names (per product, not all-or-nothing) so a re-run on
  // pageset re-init or a retry after a partial failure never wipes a fresh
  // success back to "loading" and transiently fail-closes.
  const state = getStore().getState();
  const namesNeedingFetch = MED_PRODUCT_ELIGIBILITY_PRODUCT_NAMES.filter(
    (productName) =>
      !isRequestSuccess(getProductEligibility(productName, state))
  );
  // Skip tracking on warm no-ops: the ensure action runs at multiple points in
  // a pageset, so tracking every invocation would emit duplicate events with
  // identical verdicts.
  if (namesNeedingFetch.length === 0) {
    return Promise.resolve();
  }

  return dispatch(
    loadProductEligibility({ productNames: namesNeedingFetch, retry: true })
  )
    .unwrap()
    .then(() => trackClinicalVendorEligibilityGate());
}
